import { Grid2 } from '@mui/material'
import { AsterToggleButton, AsterToggleButtonGroup } from 'asterX-UI/Buttons/toggle-button'
import { FileAccordion } from 'components/FileAccordion/FileAccordion'
import { NotFound } from 'components/NotFound/NotFound'
import { PledgeAccordion } from 'components/PledgeAccordion/PledgeAccordion'
import { useContractStore } from 'context/ContractStore'
import { differenceInYears } from 'date-fns'
import { getFomattedDate, getFullName, getFullValue } from 'lib/utils'
import React, { memo, useState } from 'react'

const collateralClientData = data => {
  if (!data) return []
  return [
    { label: 'ФИО', value: getFullName(data?.firstName, data?.lastName, data?.patronymic) || 'Нет данных' },
    { label: 'ИНН', value: data?.iin || 'Нет данных' },
    {
      label: 'Дата рождения',
      value: data?.birthDate ? getFomattedDate(data?.birthDate) : 'Нет данных'
    },
    {
      label: 'Возраст',
      value: data?.birthDate ? `${differenceInYears(new Date(), new Date(data?.birthDate))} лет` : 'Нет данных'
    },
    { label: 'Мобильный телефон', value: data?.mobilePhone1?.toString()?.toPhone() || 'Нет данных' },
    { label: 'Пол', value: data?.sexName?.name || 'Нет данных' },
    { label: 'Страна гражданства', value: data?.citizenship || 'Нет данных' },
    { label: 'Тип документа', value: data?.documentType?.name || 'Нет данных' },
    { label: 'Резиденство', value: data?.resident ? 'Резидент' : 'Нерезидент' },
    { label: 'Образование', value: data?.education?.name || 'Нет данных' },
    { label: 'Серия документа', value: data?.documentSeries || 'Нет данных' },
    { label: 'Номер документа', value: data?.documentNumber || 'Нет данных' },
    { label: 'Орган выдачи', value: data?.documentIssuer || 'Нет данных' },
    {
      label: 'Дата выдачи',
      value: data?.documentIssueDate ? getFomattedDate(data?.documentIssueDate) : 'Нет данных'
    },
    {
      label: 'Срок действия документа',
      value: data?.documentExpiryDate ? getFomattedDate(data?.documentExpiryDate) : 'Нет данных'
    },
    { label: 'Семейное положение', value: data?.maritalStatus?.name || 'Нет данных' },
    { label: 'Количество иждивенцев', value: data?.numberOfDependents?.toString() || 'Нет данных' },
    { label: 'Тип клиента', value: data?.clientType || 'Нет данных' }
  ]
}

const getRequiredData = data => {
  const type = data?.type
  switch (type) {
    case 'CLIENT':
      return [
        {
          label: 'Мобильный телефон 1',
          value: data?.clientGuarantee?.mobilePhone1?.toString()?.toPhone() || 'Нет данных'
        },
        {
          label: 'Мобильный телефон 2',
          value: data?.clientGuarantee?.mobilePhone2?.toString()?.toPhone() || 'Нет данных'
        },
        { label: 'Email', value: data?.clientGuarantee?.email || 'Нет данных' },
        { label: 'Вид деятельности', value: data?.clientGuarantee?.activityType || 'Нет данных' },
        { label: 'Название организации', value: data?.clientGuarantee?.organizationName || 'Нет данных' },
        { label: 'Должность', value: data?.clientGuarantee?.position || 'Нет данных' },
        { label: 'Позиция должности', value: data?.clientGuarantee?.jobLevel || 'Нет данных' },
        {
          label: 'Общий ежемесячный доход',
          value: data?.clientGuarantee?.monthlyIncome?.toString()?.thousandSeparator() || 'Нет данных'
        },
        {
          label: 'Стаж работы в организации (мес)',
          value: data?.clientGuarantee?.employmentDuration?.toString() || 'Нет данных'
        },
        { label: 'Общий стаж работы', value: data?.clientGuarantee?.totalExperience?.toString() || 'Нет данных' },
        { label: 'Рабочий телефон', value: data?.clientGuarantee?.workPhone?.toString()?.toPhone() || 'Нет данных' },
        {
          label: 'Сумма пенсии',
          value: data?.clientGuarantee?.pensionAmount?.toString()?.thousandSeparator() || 'Нет данных'
        }
      ]

    default:
      return []
  }
}
const getAdditionalData = data => {
  const type = data?.type
  switch (type) {
    case 'CLIENT':
      return [
        { label: 'Страна', value: data?.clientGuarantee?.country?.name || 'Нет данных' },
        { label: 'Область', value: data?.clientGuarantee?.region || 'Нет данных' },
        { label: 'Район', value: data?.clientGuarantee?.district || 'Нет данных' },
        { label: 'Сельский округ', value: data?.clientGuarantee?.ruralArea || 'Нет данных' },
        { label: 'Населённый пункт', value: data?.clientGuarantee?.settlement || 'Нет данных' },
        { label: 'Улица/Микрорайон', value: data?.clientGuarantee?.street || 'Нет данных' },
        { label: 'Дом', value: data?.clientGuarantee?.houseNumber || 'Нет данных' },
        { label: 'Квартира', value: data?.clientGuarantee?.apartment || 'Нет данных' },
        { label: 'Code KATO', value: data?.clientGuarantee?.katoCode || 'Нет данных' },
        {
          label: 'Адрес',
          value: getFullValue(
            data?.clientGuarantee?.country?.name,
            data?.clientGuarantee?.region,
            data?.clientGuarantee?.district,
            data?.clientGuarantee?.street,
            data?.clientGuarantee?.houseNumber
          )
        }
      ]
    default:
      return []
  }
}

export const getCollateralsData = data => {
  return [
    {
      title: 'Данные клиента',
      line: true,
      hasChip: true,
      data: collateralClientData(data?.clientGuarantee)
    },
    {
      title: 'Обязательная информация',
      line: true,
      data: getRequiredData(data)
    },
    {
      title: 'Дополнительная информация',
      line: true,
      data: getAdditionalData(data)
    }
  ]
}

const guaranteeBtns = [
  { label: 'Данные гаранта', code: 'guarantor-data' },
  { label: 'Файлы гаранта', code: 'guarantor-files' }
]

export const GuaranteeTab = memo(({ readonly = false }) => {
  const {
    contractStore: { loanApplicationData }
  } = useContractStore()

  const [activeTab, setActiveTab] = useState(guaranteeBtns?.[0]?.code)
  const guaranteeClients = loanApplicationData?.collaterals?.filter(item => item.type === 'CLIENT') ?? []

  if (!guaranteeClients?.length) {
    return <NotFound title="Нет гаранта" />
  }

  const groupedDocs = () => {
    return guaranteeClients.map(client => {
      const clientId = client?.clientGuaranteeId
      let docs =
        loanApplicationData?.collateralFiles?.filter(
          file => ['userUploadGuaranteeDocs',"userCreateLoanApplication"]
            .includes(file?.category?.step) && file?.collateralClientId === clientId
        ) ?? []
      const uniqueDocsMap = new Map()
      docs.forEach(doc => {
        if (!uniqueDocsMap.has(doc.id)) {
          uniqueDocsMap.set(doc.id, doc)
        }
      })

      docs = Array.from(uniqueDocsMap.values())

      return { client, docs }
    })
  }

  const groupedGuaranteeDocs = groupedDocs() || []

  return (
    <>
      <Grid2
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none!important'
          },
          gap: '10px'
        }}
      >
        <AsterToggleButtonGroup
          exclusive
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val)
          }}
        >
          {guaranteeBtns.map(({ label, code }, i) => (
            <AsterToggleButton key={`table-toggle-btn-${code}`} value={code}>
              {label}
            </AsterToggleButton>
          ))}
        </AsterToggleButtonGroup>
      </Grid2>

      {activeTab === 'guarantor-data' &&
        (guaranteeClients || [])?.map((item, index) => (
          <PledgeAccordion
            title={'Гарант'}
            key={index}
            sections={getCollateralsData(item)}
            data={item}
            index={index}
            isEdit="guarantor-data"
            readonly={readonly}
          />
        ))}

      {activeTab === 'guarantor-files' &&
        groupedGuaranteeDocs.map(({ client, docs }) => {
          const { firstName, lastName, patronymic } = client.clientGuarantee || {}
          return (
            <FileAccordion
              key={client.id}
              title={`Файлы гаранта: ${getFullName(firstName, lastName, patronymic)}`}
              data={docs}
            />
          )
        })}
    </>
  )
})
